import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  TextField as MuiTextField,
  Stack,
} from '@mui/material'
import PropTypes from 'prop-types'
import { Section, TextField } from '~/components'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import { useCopyViewerText, useParagraphContext } from '../hooks'

const ComparsionTable = (props) => {
  const { bottom } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { product, subCategory } = useRouteContext(ComparsionTable.name)
  const { canEdit, editable, paragraph } = useParagraphContext(
    ComparsionTable.name
  )
  const copyViewerText = useCopyViewerText(canEdit)
  const viewerFieldSx = useMemo(
    () => (canEdit ? undefined : { cursor: 'pointer' }),
    [canEdit]
  )

  const products = useSelector((state) =>
    bshSlice.products(state, subCategory.products)
  )

  const preparedProducts = useMemo(
    () =>
      paragraph.comparsion?.products?.map(({ id, name }) => ({
        id,
        name,
      })) ?? [],
    [paragraph.comparsion?.products]
  )
  const [selectedProducts, setSelectedProducts] = useState(preparedProducts)
  const [prevPreparedProducts, setPrevPreparedProducts] =
    useState(preparedProducts)
  if (preparedProducts !== prevPreparedProducts) {
    setPrevPreparedProducts(preparedProducts)
    setSelectedProducts(preparedProducts)
  }

  return (
    <>
      <Box
        onClick={() => copyViewerText(paragraph.text)}
        sx={{ flexGrow: 1, ...viewerFieldSx }}
      >
        <TextField
          editable={editable}
          placeholder={t('paragraph.comparsion-table.text')}
          size="small"
          sx={{ flexGrow: 1 }}
          value={paragraph.text || ''}
        />
      </Box>
      {bottom}
      {product ? (
        <Section
          sectionId={`${ComparsionTable.name}-products`}
          title={t('paragraph.comparsion-table.products', {
            name: subCategory.name,
          })}
        >
          <Stack direction="row" spacing={2}>
            <Autocomplete
              disableClearable
              disableCloseOnSelect
              filterSelectedOptions
              getOptionDisabled={(option) =>
                selectedProducts.length >= 3 &&
                !selectedProducts.find((p) => p.id === option.id)
              }
              getOptionKey={(option) => option?.id}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              limitTags={3}
              multiple
              options={products.map(({ id, name }) => ({ id, name }))}
              renderInput={(params) => (
                <MuiTextField {...params} variant="standard" />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return (
                    <Chip
                      key={key}
                      label={option.name}
                      {...tagProps}
                      disabled={option.id === product.id}
                    />
                  )
                })
              }
              sx={{ width: '300px' }}
              value={selectedProducts}
              onChange={(e, value) => {
                setSelectedProducts(value)
                dispatch(
                  bshSlice.saveComparsionProducts({
                    data: { paragraph, products: value },
                  })
                )
              }}
            />
            <Grid container spacing={1} sx={{ flexGrow: 1 }}>
              {selectedProducts.map((p, index) => (
                <Grid key={index} size={{ xs: 4 }}>
                  <picture>
                    <img src={p.thumb} />
                  </picture>
                  <span>{p.name}</span>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Section>
      ) : null}
    </>
  )
}

ComparsionTable.propTypes = {
  bottom: PropTypes.node,
}

export default ComparsionTable
